import { useCallback, useEffect, useState } from 'react'
import { postRepository } from '@/data/posts/repositories/post_repository_impl'
import type { Post } from '@/domain/posts/entities/post.entity'
import { DeletePostUseCase } from '@/domain/posts/usecases/delete_post.usecase'
import { ListPostsUseCase } from '@/domain/posts/usecases/list_posts.usecase'
import { toAppError } from '@/core/errors/app_error'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import { matchPost } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'

const listPosts = new ListPostsUseCase(postRepository)
const deletePost = new DeletePostUseCase(postRepository)

export function usePostsViewModel() {
  const [posts, setPosts] = useState<Post[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    postId: null as string | null,
    postContent: '',
  })
  const [isDeleting, setIsDeleting] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      setLoadError(null)
      setPosts(await listPosts.execute())
    } catch (error: unknown) {
      logError('Failed to load posts', error)
      setLoadError(toAppError(error, 'Failed to load posts').message)
      setPosts([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const { table, actions: tableActions } = useTableQueryState({
    items: posts,
    matchesSearch: matchPost,
  })

  const openDeleteDialog = useCallback((postId: string, postContent: string) => {
    setDeleteDialog({
      isOpen: true,
      postId,
      postContent: postContent.length > 50 ? `${postContent.substring(0, 50)}...` : postContent,
    })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ isOpen: false, postId: null, postContent: '' })
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.postId) return
    try {
      setIsDeleting(true)
      await deletePost.execute(deleteDialog.postId)
      setPosts(prev => prev.filter(post => post.id !== deleteDialog.postId))
      closeDeleteDialog()
    } catch (error: unknown) {
      logError('Failed to delete post:', error)
      alert(getAxiosLikeErrorMessage(error, 'Failed to delete post. Please try again.'))
    } finally {
      setIsDeleting(false)
    }
  }, [deleteDialog.postId, closeDeleteDialog])

  return {
    state: {
      posts,
      loadError,
      filteredPosts: table.filteredItems,
      paginatedPosts: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      deleteDialog,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    isDeleting,
    actions: {
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort: tableActions.handleSort,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
    },
  }
}
